import { useQuery } from "@tanstack/react-query";

import { api } from "../api/client";
import type { LicenceStatus } from "../api/types";
import { k, t } from "../i18n/i18n";
import { useUi } from "../state/ui";
import { LICENCE_QUERY } from "./licence";

/** Licence d'essai (demande d'Alexis du 30/10/2026, registre F44) : aucun export. Le serveur refuse toute route
 *  d'export (403 `TRIAL_EXPORT`) ; l'interface grise les mêmes gestes, sans les cacher : l'entrée « Exports » de la
 *  barre latérale, les menus « Exporter » des documents, le dossier complet, le fichier « .generex », l'EVALOPS, les
 *  exports de la situation, l'ordre reçu d'origine, l'export d'un ordre de bataille et la transmission par code. Au
 *  survol, le `title` le dit ; à l'appui (souris ou doigt), un avis le dit aussi (un `title` est invisible au doigt).
 *  Les imports restent permis. Une licence d'essai est une licence de moins de 360 jours (critère d'Alexis), relevée
 *  sur le serveur de licences par un outil root (deploy/vps/licences-essai). */

/** Code du refus du serveur. */
export const TRIAL_EXPORT = "TRIAL_EXPORT";
/** Message montré au survol et à l'appui d'un geste d'export grisé (le même que le refus du serveur). */
export const TRIAL_EXPORT_MESSAGE = k("Export impossible avec une licence d'essai.");

/** Poste d'une licence d'essai, d'après l'état de la licence (faux tant qu'il n'est pas connu). */
export function trialExport(status: Pick<LicenceStatus, "enforced" | "licensed" | "trial"> | undefined | null): boolean {
  return Boolean(status?.enforced && status.licensed && status.trial);
}

/** Événement qui montre la bulle d'essai contre le geste appuyé (`TrialTipHost`). */
export const TRIAL_TIP_EVENT = "generex:trial-tip";

/** Appui sur un geste d'export grisé : la bulle s'affiche contre lui (souris comme doigt), jamais en bas de l'écran
 *  (demande d'Alexis du 30/10/2026) ; sans élément connu (appel hors d'un clic), l'avis habituel. */
export function explainTrialExport(event?: { currentTarget?: EventTarget | null }): void {
  const target = event?.currentTarget;
  if (typeof window !== "undefined" && target && typeof (target as Element).getBoundingClientRect === "function") {
    window.dispatchEvent(new CustomEvent(TRIAL_TIP_EVENT, { detail: target }));
    return;
  }
  useUi.getState().notify({ text: t(TRIAL_EXPORT_MESSAGE), tone: "info" });
}

/** Ce qu'un geste d'export reçoit sur une licence d'essai : `blocked`, `onBlocked` (avis) et `title` ; hors essai,
 *  rien (`blocked` faux, `title` absent : le geste garde le sien). */
export interface TrialBlock {
  blocked: boolean;
  onBlocked: (event?: { currentTarget?: EventTarget | null }) => void;
  title: string | undefined;
}

export function trialBlock(trial: boolean): TrialBlock {
  return { blocked: trial, onBlocked: explainTrialExport, title: trial ? t(TRIAL_EXPORT_MESSAGE) : undefined };
}

/** Impression du navigateur (Ctrl+P) sur une licence d'essai : la page imprimée ne porte que la raison (index.css,
 *  `data-trial-print`). Posé par le portail de licence à chaque nouvel état. */
export function markTrialPrint(trial: boolean): void {
  if (typeof document === "undefined") return;
  if (trial) document.documentElement.setAttribute("data-trial-print", t(TRIAL_EXPORT_MESSAGE));
  else document.documentElement.removeAttribute("data-trial-print");
}

/** `trialBlock` de l'état de licence en cache (requête partagée avec le portail de licence). */
export function useTrialExport(): TrialBlock {
  const { data } = useQuery({ queryKey: LICENCE_QUERY, queryFn: api.licence, staleTime: 5 * 60_000 });
  return trialBlock(trialExport(data));
}
